import {
  Paragraph,
  TextRun,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from 'docx';
import { fetchImagesForPDF } from '../../utils/ImageHandler';

const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const TABLE_NO_BORDERS = { ...NO_BORDERS, insideH: NONE, insideV: NONE };

// Image target dimensions (pixels) — roughly matching the 2-column PDF layout
const IMG_W = 220;
const IMG_H = 165;

function base64ToUint8Array(dataUrl) {
  const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function detectImageType(dataUrl) {
  if (dataUrl.includes('image/png')) return 'png';
  if (dataUrl.includes('image/gif')) return 'gif';
  if (dataUrl.includes('image/bmp')) return 'bmp';
  return 'jpg';
}

export async function renderImageBlockDocx({
  data,
  children,
  rakennusData,
  sectionImageCounters,
  sectionNumber,
}) {
  const propertyId = rakennusData?.id_kiinteisto;
  if (!propertyId) return;

  if (!sectionImageCounters[sectionNumber]) sectionImageCounters[sectionNumber] = 0;

  const excluded = new Set(data?.excludedImages || []);
  const effectiveSubCategory = data.subCategoryIsLocked ? (data.subCategory ?? null) : null;

  const { availableImages, imageUrls } = await fetchImagesForPDF(propertyId, {
    category: data.category,
    subCategory: effectiveSubCategory,
  });

  const imgs = availableImages.filter(img => !excluded.has(img.id));
  if (!imgs.length) {
    children.push(new Paragraph({ children: [new TextRun('')] }));
    return;
  }

  const imageObjects = imgs
    .map(img => {
      const base64 = imageUrls[img.id];
      if (!base64?.startsWith('data:image/')) return null;
      return { id: img.id, base64, description: img.metadata?.description || '' };
    })
    .filter(Boolean);

  if (!imageObjects.length) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Kuvaa ei voitu ladata Word-tiedostoon.', size: 22 })],
        spacing: { after: 120 },
      })
    );
    return;
  }

  // Emit pairs of images as a 2-column table (mirrors PDF 2-column layout)
  for (let i = 0; i < imageObjects.length; i += 2) {
    const pair = imageObjects.slice(i, i + 2);

    const cells = pair.map(obj => {
      sectionImageCounters[sectionNumber]++;
      const num = sectionImageCounters[sectionNumber];
      const caption = `${sectionNumber}.${num}. ${obj.description}`.trim();
      const imgData = base64ToUint8Array(obj.base64);
      const imgType = detectImageType(obj.base64);

      return new TableCell({
        children: [
          new Paragraph({
            children: [
              new ImageRun({
                data: imgData,
                transformation: { width: IMG_W, height: IMG_H },
                type: imgType,
              }),
            ],
            spacing: { after: 40 },
          }),
          new Paragraph({
            children: [new TextRun({ text: caption, size: 18, italics: true })],
            spacing: { after: 120 },
          }),
        ],
        width: { size: 50, type: WidthType.PERCENTAGE },
        borders: NO_BORDERS,
      });
    });

    // Pad to 2 cells when only one image in the last row
    while (cells.length < 2) {
      cells.push(
        new TableCell({
          children: [new Paragraph({ children: [new TextRun('')] })],
          width: { size: 50, type: WidthType.PERCENTAGE },
          borders: NO_BORDERS,
        })
      );
    }

    children.push(
      new Table({
        rows: [new TableRow({ children: cells })],
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: TABLE_NO_BORDERS,
      })
    );
  }
}
